import React, { FC, useEffect, useRef } from "react";

import { useRoutes } from "@Libs/Router/hooks/useRoutes";

import { serviceRequestPathFragment } from "../../../config/routes";
import { clearDraftStorage } from "../../hooks/useDraftStorage";
import { setJustPublishedId } from "../../utils/justPublished";
import { useMinDuration } from "../../hooks/useMinDuration";
import { ProfilePicsLoaderModal } from "../ProfilePicsLoaderModal/ProfilePicsLoaderModal";

/** Floors the loader's visible time so a fast publish doesn't flash it. */
export const MIN_LOADER_DURATION_MS = 5000;

export interface ITaskCreationOutcomeModalsProps {
  isPublishing: boolean;
  /** The published request's id, or `null` while there is not one yet. */
  publishedRequestId: string | null;
}

/**
 * How a publish ends, on screen: the loader while it is in flight, then a
 * redirect to the request's show page, which opens the success modal on top
 * (HUB-5584).
 *
 * Shared by the two screens that can publish — the last config screen for a
 * logged-in user, the auth screens for a visitor who has just claimed their
 * draft — so both end the flow the same way.
 */
export const TaskCreationOutcomeModals: FC<ITaskCreationOutcomeModalsProps> = ({
  isPublishing,
  publishedRequestId,
}) => {
  const { generateRoute } = useRoutes();

  const isPublishingPadded = useMinDuration(
    isPublishing,
    MIN_LOADER_DURATION_MS
  );

  const isRedirecting = Boolean(publishedRequestId) && !isPublishingPadded;
  // A re-render while the document is unloading must not queue a second load.
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!isRedirecting || !publishedRequestId || hasRedirected.current) return;

    hasRedirected.current = true;

    setJustPublishedId(publishedRequestId);
    clearDraftStorage();
    // Never `navigate(..., { reload: true })`: that re-renders the SPA with the
    // token already cleared, and the shell's theme guard ejects to the Rails
    // category picker mid-load — the request page came back `net::ERR_ABORTED`
    // (RT-4321). `assign` touches no router state.
    window.location.assign(
      generateRoute(
        {
          fragments: serviceRequestPathFragment.SERVICE_REQUEST,
          options: { withLocale: true },
        },
        { id: publishedRequestId },
        { show_creation_confirmation: "true" }
      )
    );
  }, [isRedirecting, publishedRequestId, generateRoute]);

  // Held open through the redirect so the flow's screen never shows between
  // the loader and the request page.
  return (
    <ProfilePicsLoaderModal isOpen={isPublishingPadded || isRedirecting} />
  );
};
